import { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router";
import dayjs from "dayjs";
import { traerPrestadores, traerEspecialidades } from "./prestadoresService";
import {
  getHorariosPrestadores,
  traerTurnosPaciente,
} from "../../apiClient/rutes/turnos";
import { getMutualPorCodigo } from "../../apiClient/rutes/paciente";
import { obtenerHorariosYTurnos, manejarReservaTurno } from "./turnosService";
import {
  verificarInasistenciasPorEspecialidad,
  verificarTurnoConPrestadorPendiente,
} from "./nuevoTurnoUtils";
import { PATHS, PORTAL_PATHS } from "../../utils/constants/constants";

export const useNuevoTurno = (mostrarNotificacion) => {
  // Consolidar estados en un solo objeto para modales y feedback
  const [ui, setUI] = useState({
    cargando: true,
    abrirDesplegableEspecialidades: false,
    abrirDesplegablePrestadores: false,
    abrirModalConfirmarReserva: false,
    abrirModalPostReserva: false,
    mostrarInasisEspecilidad: false,
    mostrarTurnoPrestadorReservado: false,
  });
  const [usuario, setUsuario] = useState({});
  const [urlAxioPP, setUrlAxio] = useState("");
  const [especialidades, setEspecialidades] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [listadoPrestadores, setListadoPrestadores] = useState([]);
  const [especialidadSeleccionada, setEspecialidadSeleccionada] = useState(-1);
  const [prestadorSeleccionado, setPrestadorSeleccionado] = useState(-1);
  const [fechasFuturas, setFechasFuturas] = useState([]);
  const [fechaSeleccionada, setFechaSeleccionada] = useState("");
  const [turnosPaciente, setTurnosPaciente] = useState([]);
  const [turnosDisponibles, setTurnosDisponibles] = useState([]);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState({});
  const [notaWebMutual, setNotaWebMutual] = useState("");
  const history = useHistory();

  // Funciones y handlers principales deben ir antes de los useEffect que los usan
  const setLoading = useCallback(
    (cargando) => setUI((ui) => ({ ...ui, cargando })),
    [],
  );
  const setModal = useCallback(
    (modal, value) => setUI((ui) => ({ ...ui, [modal]: value })),
    [],
  );
  const setFeedback = useCallback(
    (feedback, value) => setUI((ui) => ({ ...ui, [feedback]: value })),
    [],
  );

  const obtenerTurnosPrestador = useCallback(
    async (prestador) => {
      setLoading(true);
      try {
        const resultado = await obtenerHorariosYTurnos(
          prestador,
          usuario,
          urlAxioPP,
        );
        if (resultado.success) {
          setFechasFuturas(resultado.turnosProximos);
          setTurnosDisponibles(resultado.turnosDisponibles);
          setFechaSeleccionada(resultado.fechaSeleccionada);
          // Si no hay turnos disponibles, mostrar notificación amarilla
          if (
            !resultado.turnosDisponibles ||
            resultado.turnosDisponibles.length === 0
          ) {
            mostrarNotificacion(
              true,
              "No hay turnos disponibles con el prestador seleccionado",
              "amarillo",
            );
          }
        } else {
          mostrarNotificacion(
            true,
            resultado.error || "No se pudieron cargar los horarios disponibles",
            "rojo",
          );
          setFechasFuturas([]);
          setTurnosDisponibles([]);
          setFechaSeleccionada("");
        }
      } catch (error) {
        mostrarNotificacion(true, "Error inesperado: " + error.message, "rojo");
      }
      setLoading(false);
    },
    [usuario, urlAxioPP, mostrarNotificacion, setLoading],
  );

  // Bandera para evitar doble apertura del modal de especialidades
  const especialidadModalAbierto = useRef(false);

  const handleChangeSelect = useCallback(
    (value, select) => {
      if (select === "Especialidad") {
        setEspecialidadSeleccionada(value);
        if (value !== -1) {
          // Filtrar prestadores por especialidad seleccionada
          let filtrados = prestadores
            .filter(
              (prestador) =>
                prestador.especialidad === value ||
                prestador.especialidad2 === value,
            )
            .map((p) => ({
              ...p,
              text: p.nombre || p.text || p.label || "",
            }));
          // Usar la función utilitaria igual que en el código original
          const prestadoresOrdenados =
            require("./nuevoTurnoUtils").ordenarPrestadoresPorProximoTurno(
              filtrados,
            );
          setListadoPrestadores(prestadoresOrdenados);
          if (verificarInasistenciasPorEspecialidad(value, turnosPaciente)) {
            setFechasFuturas([]);
            setFeedback("mostrarInasisEspecilidad", true);
            setFeedback("mostrarTurnoPrestadorReservado", false);
          } else {
            setFeedback("mostrarInasisEspecilidad", false);
          }
          setModal("abrirDesplegablePrestadores", true); // Abrir modal de profesionales aquí
        } else {
          setListadoPrestadores(prestadores);
        }
        setPrestadorSeleccionado(-1);
        setFechasFuturas([]);
        return;
      }
      if (select === "Profesional") {
        let codigoValue = value;
        if (typeof value === "object" && value !== null) {
          codigoValue = value.codigo || value.id || "";
        }
        if (typeof codigoValue !== "string") {
          codigoValue = String(codigoValue);
        }
        const [prestadorCodigo] = codigoValue.split("-");
        if (
          verificarTurnoConPrestadorPendiente(prestadorCodigo, turnosPaciente)
        ) {
          setFechasFuturas([]);
          setFeedback("mostrarTurnoPrestadorReservado", true);
          setFeedback("mostrarInasisEspecilidad", false);
        } else {
          setFeedback("mostrarTurnoPrestadorReservado", false);
          setPrestadorSeleccionado(codigoValue);
          obtenerTurnosPrestador(codigoValue);
        }
        return;
      }
      mostrarNotificacion(true, "Selección inválida", "rojo");
      return;
    },
    [
      prestadores,
      turnosPaciente,
      obtenerTurnosPrestador,
      mostrarNotificacion,
      setFeedback,
      setModal,
    ],
  );

  const cargarDesplegables = useCallback(
    async (sesion) => {
      try {
        if (!sesion || !sesion.token || !sesion.mutual) {
          console.error("[cargarDesplegables] Sesion inválida:", sesion);
          mostrarNotificacion(
            true,
            "Error: sesión inválida. No se puede continuar.",
            "rojo",
          );
          setLoading(false);
          return;
        }
        const horariosPrestadores = await getHorariosPrestadores(sesion);
        const prestadoresRegulares = await traerPrestadores(
          sesion,
          horariosPrestadores,
        );
        const prestadoresDisponibles = prestadoresRegulares.map((p) => ({
          ...p,
          text: p.nombre || p.text || p.label || "",
        }));
        setPrestadores(prestadoresDisponibles);
        setListadoPrestadores(prestadoresDisponibles);
        const especialidadesData = await traerEspecialidades(
          sesion,
          prestadoresDisponibles,
        );
        const mutualData = await getMutualPorCodigo(
          sesion.mutual,
          sesion.token,
        );
        // Filtrar solo especialidades vigentes y agregar text
        const especialidadesVigentes = Array.isArray(especialidadesData)
          ? especialidadesData
              .filter((e) => e.vigente === true)
              .map((e) => ({
                ...e,
                text: e.nombre || e.text || e.label || "",
              }))
          : [];
        setEspecialidades(especialidadesVigentes);
        setNotaWebMutual(
          (mutualData?.NotaWeb || mutualData?.notaWeb || "").trim(),
        );
      } catch (error) {
        console.error("[cargarDesplegables] Error:", error);
        mostrarNotificacion(
          true,
          "Error al cargar datos: " + error.message,
          "rojo",
        );
      }
      setLoading(false);
    },
    [mostrarNotificacion, setLoading],
  );

  // Bandera para evitar bucles de carga inicial
  const cargaInicial = useRef(false);
  useEffect(() => {
    if (cargaInicial.current) return;
    cargaInicial.current = true;
    const inicializar = async () => {
      try {
        const sesion = JSON.parse(sessionStorage.getItem("ppUL"));
        if (sesion) {
          await Promise.all([
            traerTurnosPaciente(sesion).then((turnos) =>
              setTurnosPaciente(turnos),
            ),
            cargarDesplegables(sesion),
          ]);
          setUsuario(sesion);
          setUrlAxio(localStorage.getItem("urlAxioPP"));
          document.title = localStorage.getItem("tituloWeb");
        }
      } catch (e) {
        history.push({ pathname: PATHS.ERROR });
      }
    };
    inicializar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ...existing code...

  // Bandera para evitar bucle en selección inicial de prestador externo y doble apertura de modal especialidad
  const seleccionInicial = useRef(false);
  const prestadorSeleccionadoExternamente = useRef(false);
  useEffect(() => {
    if (
      !seleccionInicial.current &&
      especialidades.length > 0 &&
      prestadores.length > 0 &&
      prestadorSeleccionado === -1
    ) {
      const prestadorExterno = history.location.state?.prestador;
      if (prestadorExterno !== undefined && prestadorExterno !== null) {
        // Buscar el objeto prestador
        const prestadorEncontrado = prestadores.find(
          (prestador) =>
            prestador.codigo.split("-")[0] === prestadorExterno.toString(),
        );
        if (prestadorEncontrado) {
          seleccionInicial.current = true;
          prestadorSeleccionadoExternamente.current = true;
          // Seleccionar especialidad
          setEspecialidadSeleccionada(prestadorEncontrado.especialidad);
          // Filtrar prestadores por especialidad
          const prestadoresFiltrados = prestadores.filter(
            (prestador) =>
              prestador.especialidad === prestadorEncontrado.especialidad ||
              prestador.especialidad2 === prestadorEncontrado.especialidad,
          );
          setListadoPrestadores(prestadoresFiltrados);
          // Seleccionar prestador
          setPrestadorSeleccionado(prestadorEncontrado.codigo);
          // Verificar inasistencias y turnos pendientes
          if (verificarInasistenciasPorEspecialidad) {
            // Validación defensiva para evitar error si turnosPaciente es undefined
            const turnosSafe = Array.isArray(turnosPaciente)
              ? turnosPaciente
              : [];
            if (
              verificarInasistenciasPorEspecialidad(
                prestadorEncontrado.especialidad,
                turnosSafe,
              )
            ) {
              setFechasFuturas([]);
              setFeedback("mostrarInasisEspecilidad", true);
              setFeedback("mostrarTurnoPrestadorReservado", false);
            } else {
              setFeedback("mostrarInasisEspecilidad", false);
              if (verificarTurnoConPrestadorPendiente) {
                if (
                  verificarTurnoConPrestadorPendiente(
                    prestadorEncontrado.codigo,
                    turnosSafe,
                  )
                ) {
                  setFechasFuturas([]);
                  setFeedback("mostrarTurnoPrestadorReservado", true);
                  setFeedback("mostrarInasisEspecilidad", false);
                } else {
                  setFeedback("mostrarTurnoPrestadorReservado", false);
                  obtenerTurnosPrestador(prestadorEncontrado.codigo);
                }
              }
            }
          }
          mostrarNotificacion(
            true,
            `Cargando horarios para ${prestadorEncontrado.nombre || prestadorEncontrado.text || prestadorEncontrado.label}...`,
            "verde",
          );
        } else {
          mostrarNotificacion(
            true,
            "El prestador seleccionado no está disponible, seleccione una especialidad",
            "amarillo",
          );
          setModal("abrirDesplegableEspecialidades", true);
        }
        history.location.state = {};
      } else {
        if (
          especialidadSeleccionada === -1 &&
          !especialidadModalAbierto.current
        ) {
          const unicaEspecialidad =
            especialidades.length === 1 ? especialidades[0] : null;
          const codigoEspecialidadUnica =
            unicaEspecialidad?.codigo ?? unicaEspecialidad?.id;

          if (
            codigoEspecialidadUnica !== undefined &&
            codigoEspecialidadUnica !== null
          ) {
            seleccionInicial.current = true;
            especialidadModalAbierto.current = true;
            handleChangeSelect(codigoEspecialidadUnica, "Especialidad");
          } else {
            especialidadModalAbierto.current = true;
            setModal("abrirDesplegableEspecialidades", true);
          }
        }
        prestadorSeleccionadoExternamente.current = false;
      }
    }
  }, [
    prestadores,
    especialidades,
    history,
    prestadorSeleccionado,
    especialidadSeleccionada,
    mostrarNotificacion,
    setModal,
    setEspecialidadSeleccionada,
    setListadoPrestadores,
    setPrestadorSeleccionado,
    setFeedback,
    setFechasFuturas,
    obtenerTurnosPrestador,
    turnosPaciente,
    handleChangeSelect,
  ]);

  // Handler para reservar turno
  const handleClickConfirmarHacerReserva = useCallback(async () => {
    setLoading(true);
    try {
      const resultado = await manejarReservaTurno(
        turnoSeleccionado,
        usuario,
        prestadorSeleccionado,
        urlAxioPP,
      );
      if (resultado.success) {
        mostrarNotificacion(
          true,
          resultado.mensaje || "El turno se reservó correctamente",
          "verde",
        );
        setModal("abrirModalConfirmarReserva", false);
        setFechaSeleccionada("");
        setTurnosDisponibles([]);
        setModal("abrirModalPostReserva", true);
      } else {
        mostrarNotificacion(
          true,
          resultado.mensaje ||
            "Ocurrió un problema al intentar reservar el turno",
          "rojo",
        );
      }
    } catch (error) {
      mostrarNotificacion(true, "Error inesperado: " + error.message, "rojo");
    }
    setLoading(false);
  }, [
    turnoSeleccionado,
    usuario,
    prestadorSeleccionado,
    urlAxioPP,
    mostrarNotificacion,
    setLoading,
    setModal,
  ]);

  // Handler para seleccionar turno
  const handleClickSeleccionarTurno = useCallback(
    (turno) => {
      setTurnoSeleccionado(turno);
      setModal("abrirModalConfirmarReserva", true);
    },
    [setModal],
  );

  // Handler para cambiar fecha
  const handleChangeFecha = useCallback((value) => {
    setFechaSeleccionada(value);
  }, []);

  // Handler para copiar teléfono
  const handleClickCopiarTelefono = useCallback(() => {
    const phoneNumber = "08105552765";
    navigator.clipboard.writeText(phoneNumber);
    mostrarNotificacion(
      true,
      "Se ha copiado el número de teléfono a su portapapeles",
      "",
    );
  }, [mostrarNotificacion]);

  // Handler para post reserva
  const handlePostTurnoReservado = useCallback(
    (accion) => {
      setTurnoSeleccionado({});
      setModal("abrirModalPostReserva", false);
      switch (accion) {
        case "nuevoTurno":
          window.location.reload();
          break;
        case "turnosPaciente":
          history.push({ pathname: PORTAL_PATHS.TURNOS });
          break;
        default:
          history.push({ pathname: PORTAL_PATHS.TURNOS });
          break;
      }
    },
    [setModal, history],
  );

  // Función para obtener datos del prestador seleccionado
  const getPrestadorSeleccionadoData = useCallback(() => {
    return (
      prestadores.find(
        (prestador) => prestador.codigo === prestadorSeleccionado,
      ) || {}
    );
  }, [prestadores, prestadorSeleccionado]);

  // Arrays mapeados para los desplegables, siempre con {codigo, text}
  const especialidadesDesplegable = especialidades.map((e) => ({
    codigo: e.codigo ?? e.id ?? "",
    text: e.text ?? e.nombre ?? e.label ?? "",
    ...e,
  }));
  const prestadoresDesplegable = listadoPrestadores.map((p) => ({
    codigo: p.codigo ?? p.id ?? "",
    text: p.text ?? p.nombre ?? p.label ?? "",
    ...p,
  }));

  // Return - solo los valores necesarios para el componente
  return {
    usuario,
    cargando: ui.cargando,
    especialidades,
    prestadores,
    listadoPrestadores,
    especialidadesDesplegable,
    prestadoresDesplegable,
    especialidadSeleccionada,
    prestadorSeleccionado,
    fechasFuturas,
    fechaSeleccionada,
    turnosDisponibles,
    turnoSeleccionado,
    notaWebMutual,
    mostrarInasisEspecilidad: ui.mostrarInasisEspecilidad,
    mostrarTurnoPrestadorReservado: ui.mostrarTurnoPrestadorReservado,
    abrirDesplegableEspecialidades: ui.abrirDesplegableEspecialidades,
    abrirDesplegablePrestadores: ui.abrirDesplegablePrestadores,
    abrirModalConfirmarReserva: ui.abrirModalConfirmarReserva,
    abrirModalPostReserva: ui.abrirModalPostReserva,
    setEspecialidadSeleccionada,
    setPrestadorSeleccionado,
    setFechaSeleccionada,
    setTurnoSeleccionado,
    setAbrirDesplegableEspecialidades: (valor) =>
      setUI((ui) => ({ ...ui, abrirDesplegableEspecialidades: valor })),
    setAbrirDesplegablePrestadores: (valor) =>
      setUI((ui) => ({ ...ui, abrirDesplegablePrestadores: valor })),
    setAbrirModalConfirmarReserva: (valor) =>
      setUI((ui) => ({ ...ui, abrirModalConfirmarReserva: valor })),
    setAbrirModalPostReserva: (valor) =>
      setUI((ui) => ({ ...ui, abrirModalPostReserva: valor })),
    handleChangeSelect,
    handleChangeFecha,
    handleClickSeleccionarTurno,
    handleClickConfirmarHacerReserva,
    handleClickCopiarTelefono,
    handlePostTurnoReservado,
    getPrestadorSeleccionadoData,
    setModal,
    setFeedback,
  };
};
